import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { isAdminLike } from '../context/AuthContext';
import { styles, fmtDate } from './UI';
import DropZone from './DropZone';

const fmtSize = (b) => {
  if (b == null) return '';
  if (b >= 1_000_000) return (b / 1_000_000).toFixed(1) + ' MB';
  if (b >= 1_000) return (b / 1_000).toFixed(0) + ' KB';
  return b + ' B';
};

// Reused for both opportunities (projectId) and Activity Log entries
// (callLogId). Exactly one of the two ids should be provided. Activity files
// dual-home onto the linked opportunity server-side, so the same component
// surfaces them in both places. Download/delete key off the attachment id and
// are identical for both.
export default function Attachments({ projectId, callLogId }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');

  const isActivity = callLogId != null;
  const scopeId = isActivity ? callLogId : projectId;

  const reload = () => (isActivity
    ? api.listActivityAttachments(callLogId)
    : api.listAttachments(projectId)
  ).then(setFiles).catch(e => setError(e.message));
  useEffect(() => { if (scopeId) reload(); }, [scopeId]);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      if (isActivity) await api.uploadActivityAttachment(callLogId, file);
      else await api.uploadAttachment(projectId, file);
      reload();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const onUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    uploadFile(file);
  };

  const onDownload = async (f) => {
    setDownloading(f.id); setError('');
    try {
      await api.downloadAttachment(f.id, f.original_name);
    } catch (err) { setError(err.message); }
    finally { setDownloading(null); }
  };

  const onDelete = async (id) => {
    if (!confirm('Delete this file?')) return;
    try {
      await api.deleteAttachment(id);
      reload();
    } catch (err) { setError(err.message); }
  };

  const canDelete = (f) => isAdminLike(user) || user?.id === f.uploaded_by;

  if (!scopeId) return null;

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid #E5E7EB` }}>
      <h2 style={styles.h2}>Attachments</h2>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ marginBottom: 12 }}>
        <DropZone onFiles={files => uploadFile(files[0])} disabled={busy} title="Drop a file here" style={{ padding: 4 }}>
          <input type="file" onChange={onUpload} disabled={busy} />
        </DropZone>
        {busy && <span style={{ marginLeft: 8, color: '#6B7280' }}>Uploading...</span>}
        {isActivity && (
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            Documents and saved emails (.msg / .eml / .pdf). Files also show on the linked opportunity.
          </div>
        )}
      </div>
      {files.length === 0 ? (
        <p style={{ color: '#6B7280', fontSize: 13, margin: 0 }}>No files uploaded yet.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>File</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Uploaded</th>
              <th style={styles.th}>By</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {files.map(f => (
              <tr key={f.id}>
                <td style={styles.td}>
                  <button
                    style={{ background: 'none', border: 'none', color: '#E36F2C', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 'inherit' }}
                    disabled={downloading === f.id}
                    onClick={() => onDownload(f)}
                  >
                    {downloading === f.id ? 'Downloading…' : f.original_name}
                  </button>
                </td>
                <td style={styles.td}>{fmtSize(f.size_bytes)}</td>
                <td style={styles.td}>{fmtDate(f.created_at)}</td>
                <td style={styles.td}>{f.uploaded_by_name}</td>
                <td style={styles.td}>
                  {canDelete(f) && (
                    <button style={styles.btnDanger} onClick={() => onDelete(f.id)}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
